import axios from 'axios';

import { Id, JobTitle, getData } from '@feature/utils';

import {
  IndicatorItemOption,
  IndicatorItemRequest,
  IndicatorResult,
  MinStdAnnexTemplate,
  MinStdAvailableAnnex,
  MinStdReport,
  MinStdRequest,
  MinStdResultReport,
} from './MinStdTypes';

export function getMinStdItemFileDownloadUrl(authorId: Id, indicatorCode: string, itemId: Id) {
  return `/api/min-std-report/author/${authorId}/indicator/${indicatorCode}/item/${itemId}/file-download`;
}

export function getMinStdDownloadableReportUrl(
  authorId: Id,
  jobTitle: JobTitle | null,
  startYear: number,
  endYear: number,
  fileType: string,
) {
  return `/api/min-std-report/author/${authorId}/downloadable-report?fileType=${fileType}&startYear=${startYear}&endYear=${endYear}&jt=${jobTitle}`;
}

export function get(authorId: Id) {
  return axios
    .get<MinStdReport | null>(`/min-std-report/author/${authorId}`)
    .then(getData)
    .then(data => (!data ? null : data));
}

export function open(request: MinStdRequest) {
  return axios.post<unknown>(`/min-std-report/current-author`, request).then(getData);
}

export function getAvailableAnnexes() {
  return axios.get<MinStdAvailableAnnex[]>(`/min-std-report/available-annex`).then(getData);
}

export function getMinStdAnnexTemplate(annexNo: number) {
  return axios.get<MinStdAnnexTemplate>(`/min-std-report/annex/${annexNo}/template`).then(getData);
}

export function getResult(authorId: Id, jobTitle: JobTitle | null, startYear: number, endYear: number) {
  return axios
    .get<MinStdResultReport>(`/min-std-report/author/${authorId}/result`, {
      params: {
        jobTitle,
        endYear,
        startYear,
      },
    })
    .then(getData);
}

export function getIndicatorResult(
  authorId: Id,
  indicatorCode: string,
  jobTitle: JobTitle | null,
  year: number | null,
) {
  return axios
    .get<IndicatorResult>(`/min-std-report/author/${authorId}/indicator/${indicatorCode}/result`, {
      params: {
        jobTitle,
        year,
      },
    })
    .then(getData);
}

export function addIndicatorItem(authorId: Id, indicatorCode: string, request: IndicatorItemRequest) {
  const formData = new FormData();
  if (request.newFile != null) {
    formData.append('file', request.newFile);
  }
  formData.append('payload', new Blob([JSON.stringify(request)], { type: 'application/json' }));
  return axios
    .post<unknown>(`/min-std-report/author/${authorId}/indicator/${indicatorCode}/item`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(getData);
}

export function editIndicatorItem(authorId: Id, indicatorCode: string, itemId: Id, request: IndicatorItemRequest) {
  const formData = new FormData();
  if (request.newFile != null) {
    formData.append('file', request.newFile);
  }
  formData.append('payload', new Blob([JSON.stringify(request)], { type: 'application/json' }));
  return axios
    .put<unknown>(`/min-std-report/author/${authorId}/indicator/${indicatorCode}/item/${itemId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(getData);
}

export function deleteIndicatorItem(authorId: Id, indicatorCode: string, itemId: Id) {
  return axios.delete(`/min-std-report/author/${authorId}/indicator/${indicatorCode}/item/${itemId}`).then(getData);
}

export function getPossibleItemOptions<T>(authorId: Id, indicatorCode: string, year: number | null) {
  return axios
    .get<IndicatorItemOption<T>[]>(`/min-std-report/author/${authorId}/indicator/${indicatorCode}/possible-options`, {
      params: {
        year,
      },
    })
    .then(getData);
}

export function addAllPossibleItemOptions(authorId: Id, indicatorCode: string, year: number | null) {
  return axios
    .put<unknown>(`/min-std-report/author/${authorId}/indicator/${indicatorCode}/add-all-possible-options`, undefined, {
      params: {
        year,
      },
    })
    .then(getData);
}
